import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Settings, Plus, Search, Loader2, Trash2, Edit3, DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FALLBACK_CURRENCY_RATES } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const emptyForm = {
  currency: "USD",
  s_and_t_rate_local: 0,
  s_and_t_rate_foreign: 0,
  conversion_rate_to_zar: 0,
  effective_from: "",
  effective_to: "",
};

export default function TravellingAdminCurrency() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCurrency, setFilterCurrency] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCurrency !== "all") params.currency = filterCurrency;
      const res = await axios.get(`${API}/travelling/currency-rates`, { params, headers: getHeaders() });
      setRates(res.data.length > 0 ? res.data : FALLBACK_CURRENCY_RATES);
    } catch { setRates(FALLBACK_CURRENCY_RATES); toast.error("Failed to load. Showing demo data."); }
    setLoading(false);
  }, [filterCurrency]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const openAdd = () => { setEditId(null); setForm({ ...emptyForm }); setDialogOpen(true); };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      currency: r.currency,
      s_and_t_rate_local: r.s_and_t_rate_local,
      s_and_t_rate_foreign: r.s_and_t_rate_foreign,
      conversion_rate_to_zar: r.conversion_rate_to_zar,
      effective_from: r.effective_from?.slice(0, 10) || "",
      effective_to: r.effective_to?.slice(0, 10) || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.effective_from) { toast.error("Effective from date required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/travelling/currency-rates/${editId}`, form, { headers: getHeaders() });
        toast.success("Rate updated");
      } else {
        await axios.post(`${API}/travelling/currency-rates`, form, { headers: getHeaders() });
        toast.success("Rate created");
      }
      setDialogOpen(false);
      fetchRates();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/travelling/currency-rates/${deleteId}`, { headers: getHeaders() });
      toast.success("Deleted");
      setDeleteId(null);
      fetchRates();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">CURRENCY ADMIN</h1>
            <p className="text-slate-600">S&T rates & currency conversion admin panel</p>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">USD S&T Rate</p>
          <p className="text-2xl font-bold text-slate-800">
            {rates.find(r => r.currency === "USD")?.s_and_t_rate_foreign || "-"}
          </p>
          <p className="text-xs text-slate-400">Foreign per day</p>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">EUR S&T Rate</p>
          <p className="text-2xl font-bold text-slate-800">
            {rates.find(r => r.currency === "EUR")?.s_and_t_rate_foreign || "-"}
          </p>
          <p className="text-xs text-slate-400">Foreign per day</p>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Local S&T Rate (ZAR)</p>
          <p className="text-2xl font-bold text-slate-800">
            {rates.find(r => r.currency === "USD")?.s_and_t_rate_local || rates.find(r => r.currency === "EUR")?.s_and_t_rate_local || "-"}
          </p>
          <p className="text-xs text-slate-400">ZAR per day</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3">
        <Select value={filterCurrency} onValueChange={setFilterCurrency}>
          <SelectTrigger className="w-36 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Currency" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={openAdd} className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-none text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Rate
        </Button>
      </div>

      {/* Rates Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : rates.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No currency rates configured. Add a rate to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                {["Currency", "S&T Rate (Local)", "S&T Rate (Foreign)", "Conv Rate to ZAR", "Effective", "Updated", ""].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3 font-medium text-slate-800">{r.currency}</td>
                  <td className="p-3 text-slate-600">{r.s_and_t_rate_local}</td>
                  <td className="p-3 text-slate-600">{r.s_and_t_rate_foreign}</td>
                  <td className="p-3 text-slate-600">{r.conversion_rate_to_zar}</td>
                  <td className="p-3 text-xs text-slate-500">{r.effective_from?.slice(0,10)} → {r.effective_to?.slice(0,10)}</td>
                  <td className="p-3 text-xs text-slate-400">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "-"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-sky-600"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(r.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">{editId ? "Edit" : "Add"} Currency Rate</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Select value={form.currency} onValueChange={(v) => setForm(f => ({ ...f, currency: v }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Currency" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" step="0.01" placeholder="S&T Rate (Local ZAR)" value={form.s_and_t_rate_local} onChange={(e) => setForm(f => ({ ...f, s_and_t_rate_local: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <Input type="number" step="0.01" placeholder="S&T Rate (Foreign Currency)" value={form.s_and_t_rate_foreign} onChange={(e) => setForm(f => ({ ...f, s_and_t_rate_foreign: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <Input type="number" step="0.01" placeholder="Conversion Rate to ZAR" value={form.conversion_rate_to_zar} onChange={(e) => setForm(f => ({ ...f, conversion_rate_to_zar: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" label="Effective From" value={form.effective_from} onChange={(e) => setForm(f => ({ ...f, effective_from: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              <Input type="date" label="Effective To" value={form.effective_to} onChange={(e) => setForm(f => ({ ...f, effective_to: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> {editId ? "Update Rate" : "Create Rate"}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Delete Rate?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
